import { useState, useEffect, useRef } from 'react';

interface OnlineReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const referralTypes = [
  'Medicare',
  'DVA',
  'NDIS',
  'Insurance Referral',
  'Workcover/Worksafe',
  'Specialist Referral',
  'Others',
];

const referralRequirements: Record<string, { title: string; requirements: string[] }> = {
  Medicare: {
    title: 'Medicare Referral',
    requirements: [
      'Please attach GP referral and Chronic Disease Management (CDM) Plan and provide a referral form (also known as an Enhanced Primary Care [EPC] referral).',
      'This enables us to charge Medicare directly for your patients Exercise Physiology sessions.',
    ],
  },
  DVA: {
    title: 'DVA Referral',
    requirements: [
      'Please attach GP referral using the D0904 form, listing accepted conditions.',
      'This enables us to charge DVA directly for your patients Exercise Physiology sessions.',
    ],
  },
  NDIS: {
    title: 'NDIS Referral',
    requirements: [
      'Please attach your GP referral that includes diagnosis and outlines how your patients disability affects functional capacity.',
      'This documentation allows us to demonstrate the clinical need for Exercise Physiology services and charge directly to NDIS for your patients Exercise Physiology Sessions.',
    ],
  },
  'Insurance Referral': {
    title: 'Insurance Referral',
    requirements: [],
  },
  'Workcover/Worksafe': {
    title: 'Workcover/Worksafe Referral',
    requirements: [
      'Please attach referral and Workcover/Worksafe Certificate.',
    ],
  },
  'Specialist Referral': {
    title: 'Specialist Referral',
    requirements: [
      'Please attached your written referral to access Medicare-funded Exercise Physiology sessions for patients with chronic or complex care needs',
      'The referral should specify the reason for the assessment and, if possible, the recommended number of sessions.',
      'Individual sessions: Up to five sessions per calendar year under Medicare.',
      'Group sessions (Type 2 Diabetes): Up to eight sessions per year.',
    ],
  },
  Others: {
    title: 'Other Referral',
    requirements: [
      'Please attached your written referral and complete the funding information',
    ],
  },
};

const fundingOptions = [
  'Private Health Insurance',
  'Self-Funded',
  'Other Government Funding',
  'Community/Charity Program',
];

export default function OnlineReferralModal({ isOpen, onClose }: OnlineReferralModalProps) {
  const [selectedType, setSelectedType] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedFunding, setSelectedFunding] = useState('');
  const [fundingDropdownOpen, setFundingDropdownOpen] = useState(false);
  const [uploadConfirmed, setUploadConfirmed] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    patientName: '',
    patientPhone: '',
    patientDob: '',
    claimNumber: '',
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    setDropdownOpen(false);
    setShowForm(true);
  };

  const handleBack = () => {
    setShowForm(false);
    setSelectedType('');
    setFormData({ patientName: '', patientPhone: '', patientDob: '', claimNumber: '' });
    setSelectedFunding('');
    setUploadConfirmed(false);
    setUploadedFiles([]);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileNames = Array.from(files).map((f) => f.name);
      setUploadedFiles((prev) => [...prev, ...fileNames]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !formData.patientName || !formData.patientPhone || !formData.patientDob || !uploadConfirmed) return;
    if (selectedType === 'Others' && !selectedFunding) return;

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('referralType', selectedType);
      body.append('patientName', formData.patientName);
      body.append('patientPhone', formData.patientPhone);
      body.append('patientDob', formData.patientDob);
      body.append('claimNumber', formData.claimNumber);
      if (selectedType === 'Others') {
        body.append('fundingType', selectedFunding);
      }
      body.append('uploadedDocuments', uploadedFiles.length > 0 ? uploadedFiles.join(', ') : 'Uncollectable');

      const res = await fetch('https://readdy.ai/api/form/d65ttbsc5bvcefa8qrrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
      }
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setSelectedType('');
      setDropdownOpen(false);
      setShowForm(false);
      setFormData({ patientName: '', patientPhone: '', patientDob: '', claimNumber: '' });
      setSelectedFunding('');
      setUploadConfirmed(false);
      setUploadedFiles([]);
    }, 300);
  };

  if (!isOpen) return null;

  const currentRequirements = referralRequirements[selectedType];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[640px] mx-4 max-h-[90vh] overflow-y-auto animate-fadeIn">
        {submitted ? (
          <div className="px-8 py-16 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-green-100 rounded-full">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Referral Submitted</h3>
            <p className="text-sm text-gray-500 mb-6">Thank you! We will be in touch shortly.</p>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : !showForm ? (
          /* Initial Selection View */
          <div className="p-8">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Online Referral Form</h2>
                <p className="text-sm text-gray-500 mt-1">Accelerated Recovery Centre (ARC)</p>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-full hover:bg-gray-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Info Banner */}
            <div className="bg-[#3B52C9] text-white rounded-xl p-5 mb-6">
              <h3 className="text-base font-bold mb-2">
                Referral Pathway – Accelerated Recovery Centre (ARC)
              </h3>
              <p className="text-sm leading-relaxed opacity-95">
                The Accelerated Recovery Centre (ARC) provides multidisciplinary, evidence-based rehabilitation and exercise physiology services for veterans and the wider community. Referrals are accepted via{' '}
                <strong>Medicare</strong>, the{' '}
                <strong>Department of Veterans&apos; Affairs (DVA)</strong>, the{' '}
                <strong>National Injury Insurance Scheme (QLD)</strong>,{' '}
                <strong>Workcover</strong>, <strong>Worksafe</strong>, and{' '}
                <strong>Specialist referrals</strong>.
              </p>
            </div>

            {/* Select Referral Type */}
            <div className="bg-gray-50 rounded-xl p-5">
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Select Referral Type
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg px-5 py-3 text-sm text-left cursor-pointer hover:border-gray-300 transition-colors"
                >
                  <span className={selectedType ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedType || 'Choose your referral type...'}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${
                      dropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-20">
                    {referralTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeSelect(type)}
                        className="w-full text-left px-5 py-2.5 text-sm transition-colors cursor-pointer text-gray-700 hover:bg-gray-50"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="mt-6 bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
              <p className="text-base font-semibold text-gray-900 mb-3">
                Accelerated Recovery Centre (ARC)
              </p>
              <div className="space-y-2">
                <a
                  href="mailto:enquire@archealth.net.au"
                  className="block text-[#3B82F6] hover:underline text-sm font-medium cursor-pointer"
                >
                  enquire@archealth.net.au
                </a>
                <a
                  href="https://www.archealth.net.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-[#3B82F6] hover:underline text-sm font-medium cursor-pointer"
                >
                  www.archealth.net.au
                </a>
              </div>
            </div>
          </div>
        ) : (
          /* Form View after selection */
          <form
            id="online-referral-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="p-8"
          >
            {/* Back Button & Title */}
            <div className="flex items-center mb-6">
              <button
                type="button"
                onClick={handleBack}
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer mr-3"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <h2 className="text-xl font-bold text-gray-900">{currentRequirements?.title}</h2>
            </div>

            {/* Referral Requirements */}
            {currentRequirements?.requirements.length > 0 && (
              <div className="bg-[#EEF2FF] rounded-xl p-5 mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Referral Requirements:</h4>
                <ul className="space-y-2">
                  {currentRequirements.requirements.map((req, index) => (
                    <li key={index} className="flex items-start text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Funding Type for Others */}
            {selectedType === 'Others' && (
              <div className="bg-[#EEF2FF] rounded-xl p-5 mb-6">
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  I am applying for funding under: *
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setFundingDropdownOpen(!fundingDropdownOpen)}
                    className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg px-5 py-3 text-sm text-left cursor-pointer hover:border-gray-300 transition-colors"
                  >
                    <span className={selectedFunding ? 'text-gray-900' : 'text-gray-400'}>
                      {selectedFunding || 'Select funding type...'}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${
                        fundingDropdownOpen ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>

                  {fundingDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-20">
                      {fundingOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            setSelectedFunding(option);
                            setFundingDropdownOpen(false);
                          }}
                          className={`w-full text-left px-5 py-2.5 text-sm transition-colors cursor-pointer ${
                            selectedFunding === option
                              ? 'bg-blue-50 text-[#3B52C9] font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input type="hidden" name="fundingType" value={selectedFunding} />
              </div>
            )}

            {/* Patient Details */}
            <div className="border border-gray-200 rounded-xl p-5 mb-6">
              <h4 className="text-sm font-bold text-gray-900 mb-4">Patient details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Patient name *</label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#3B52C9] focus:ring-1 focus:ring-[#3B52C9] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Patient phone number *</label>
                  <input
                    type="tel"
                    name="patientPhone"
                    value={formData.patientPhone}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#3B52C9] focus:ring-1 focus:ring-[#3B52C9] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Patient date of birth *</label>
                  <input
                    type="text"
                    name="patientDob"
                    value={formData.patientDob}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 01/01/1990"
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#3B52C9] focus:ring-1 focus:ring-[#3B52C9] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Claim number</label>
                  <input
                    type="text"
                    name="claimNumber"
                    value={formData.claimNumber}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#3B52C9] focus:ring-1 focus:ring-[#3B52C9] transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Supporting Documents */}
            <div className="bg-[#EEF2FF] rounded-xl p-5 mb-6">
              <h4 className="text-sm font-bold text-gray-900 mb-2">Supporting Documents</h4>
              <p className="text-sm font-medium text-gray-700 mb-1">Upload Supporting Documents</p>
              <p className="text-sm text-gray-500 mb-4">Please upload documents as per the referral requirements.</p>
              
              <a
                href="https://songxz.top"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-5 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap mb-4"
              >
                <i className="ri-upload-2-line mr-2"></i>
                Upload Documents
              </a>

              {uploadedFiles.length > 0 && (
                <div className="mb-4 space-y-1">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="text-sm text-gray-600 flex items-center">
                      <i className="ri-file-line mr-2 text-gray-400"></i>
                      {file}
                    </div>
                  ))}
                </div>
              )}

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={uploadConfirmed}
                  onChange={(e) => setUploadConfirmed(e.target.checked)}
                  className="w-4 h-4 mt-0.5 mr-3 rounded border-gray-300 text-[#3B82F6] focus:ring-[#3B82F6] cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  I confirm that I&apos;ve uploaded the referrals via the system *
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  submitting ||
                  !formData.patientName ||
                  !formData.patientPhone ||
                  !formData.patientDob ||
                  !uploadConfirmed ||
                  (selectedType === 'Others' && !selectedFunding)
                }
                className="px-6 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Submitting...' : `Submit ${selectedType} Referral`}
              </button>
            </div>

            <input type="hidden" name="referralType" value={selectedType} />
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}
